import axios from "axios";
import { baseUrl } from "../helpers/Url";
import { Roles } from "../helpers/Common";
import { removeDashboardFiltersFromLocalStorage } from "../components/organism/dashboardFilters/dashboardFilters";

const TOKEN_URL = `${baseUrl}/connect/token`;
const REGISTER_URL = `${baseUrl}/api/userManager/registro`;
const VERIFY_URL = `${baseUrl}/api/userManager/verificar`;

class AuthService {
  login(credentials) {
    return axios.post(
      TOKEN_URL,
      `token=${credentials.token}&grant_type=token&version=1`
    );
  }

  register(nombre, apellidos, empresa, cargo, telefono, email, partner) {
    return axios.post(REGISTER_URL, null, {
      params: {
        nombre,
        apellidos,
        empresa,
        cargo,
        telefono,
        email,
        partner,
      },
    });
  }

  verify(token) {
    return axios.post(VERIFY_URL, null, {
      params: { token },
    });
  }

  logOut() {
    localStorage.setItem("fromLogOut", "true");
    this.removeUserInfo();
    removeDashboardFiltersFromLocalStorage();
    localStorage.removeItem("reportFilters");
    // Old keys
    localStorage.removeItem("partnerAccesos");
    localStorage.removeItem("plantaAccesos");
    localStorage.removeItem("zonaAccesos");
    localStorage.removeItem("dashBoardFilters");
    localStorage.removeItem("dashboarFilters");
  }

  getUserInfo() {
    const tokenInfoString = localStorage.getItem("tokenInfo");

    if (!tokenInfoString) {
      return null;
    }

    const tokenInfo = JSON.parse(tokenInfoString);

    return tokenInfo;
  }

  setOnLocalStorage(name, value) {
    try {
      localStorage.setItem(name, value);
    } catch (error) {
      console.error(error);
    }
  }

  setUserInfo(userInfo) {
    this.setOnLocalStorage("tokenInfo", JSON.stringify(userInfo));
  }

  removeUserInfo() {
    localStorage.removeItem("tokenInfo");
  }

  /**
   * @returns {boolean}
   */
  isAuthenticated() {
    const userInfo = this.getUserInfo();
    if (userInfo === null) return false;

    if (userInfo.expireDate === undefined) return true;
    else return new Date(userInfo.expireDate) >= new Date();
  }

  /**
   * @returns {boolean}
   */
  isInRole(role) {
    const userInfo = this.getUserInfo();
    if (userInfo === null) return false;

    return userInfo.roles.includes(role);
  }

  getRole() {
    const userInfo = this.getUserInfo();
    if (userInfo === null) return null;

    switch (userInfo.roles[0]) {
      case "keyuser":
        return Roles.Admin;
      case "dsc":
        return Roles.DSC;
      case "partner":
        return Roles.Partner;
      case "endcustomer":
        return Roles.EndCustomer;
      default:
        return null;
    }
  }

  /**
   * @returns {boolean}
   */
  hasRoleAdmin() {
    return this.isInRole(Roles.Admin);
  }

  /**
   * @returns {boolean}
   */
  hasRoleDSC() {
    return this.isInRole(Roles.DSC);
  }

  /**
   * @returns {boolean}
   */
  hasRolePartner() {
    return this.isInRole(Roles.Partner);
  }

  /**
   * @returns {boolean}
   */
  hasRoleEndCustomer() {
    return this.isInRole(Roles.EndCustomer);
  }
  
  updateUserAccessPermisions(userAccessPermissions) {
    const userInfo = this.getUserInfo();
    if (userInfo === null) {
      return false;
    }
    let updateTokenInfo = false;

    if (!userAccessPermissions.roles || userAccessPermissions.disabled) {
      this.logOut();
      window.location.replace("/unauthorized");
    }

    const isEqualsJson = (obj1, obj2) => {
      if (!obj1) {
        obj1 = [];
      }
      if (!obj2) {
        obj2 = [];
      }
      const keys1 = Object.keys(obj1);
      const keys2 = Object.keys(obj2);

      /*return true when the two json has same length and 
      all the properties has same value key by key*/
      return (
        keys1.length === keys2.length &&
        Object.keys(obj1).every((key) => obj1[key] === obj2[key])
      );
    };

    if (userInfo.isReadOnly !== userAccessPermissions.isReadOnly) {
      userInfo.isReadOnly = userAccessPermissions.isReadOnly;
      updateTokenInfo = true;
    }
    if (userInfo.isPremium !== userAccessPermissions.isPremium) {
      userInfo.isPremium = userAccessPermissions.isPremium;
      updateTokenInfo = true;
    }
    if (userInfo.isRegionManager !== userAccessPermissions.isRegionManager) {
      userInfo.isRegionManager = userAccessPermissions.isRegionManager;
      updateTokenInfo = true;
    }
    if (userInfo.isGlobal !== userAccessPermissions.isGlobal) {
      userInfo.isGlobal = userAccessPermissions.isGlobal;
      updateTokenInfo = true;
    }

    if (!isEqualsJson(userInfo.roles, userAccessPermissions.roles)) {
      this.logOut();
      window.location.replace("/login");
    }

    if (updateTokenInfo) {
      this.setUserInfo(userInfo);
    }
  }

  /**
   * @returns {number}
   */
  getPartnerId() {
    const userInfo = this.getUserInfo();
    let partnerId = 0;

    if (userInfo === null) {
      return partnerId;
    }

    partnerId = userInfo?.partnerId;

    return isNaN(partnerId) ? 0 : partnerId;
  }

  /**
   * @returns {number}
   */
  getCountryId() {
    const userInfo = this.getUserInfo();
    let countryId = 1;

    if (userInfo === null) {
      return countryId;
    }

    countryId = userInfo?.countryid;

    if (countryId === null || countryId === undefined) {
      console.error("Error in user => CountryId is null or undefined");
    }

    return isNaN(countryId) ? 1 : countryId;
  }

  /**
   * Comprueba si el usuario es premium y en caso de ser EndUser, si tiene acceso a plantas premium.
   * @returns {number} 0 - No es premium, 1 - Es premium, 2 - Tiene plantas premium, pero no todas ellas.
   */
  userPremium() {
    const userInfo = this.getUserInfo();

    if (userInfo === null) {
      return 0;
    }

    return userInfo.isPremium;
  }

}

export default new AuthService();
